import { nextTick, onMounted, reactive, ref, toRef, toRefs, unref, watch } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { concat, get } from 'lodash'

type ListQuery =
  | (<T = AnyObject[] | AnyObject>(
      query: any
    ) => Promise<{
      code?: number
      data?: T | any
      rows: T | any
    }>)
  | undefined

type responseType = { code?: number; data?: AnyObject[] | AnyObject }

type Options = {
  getList: ListQuery | Ref<ListQuery> | ComputedRef<ListQuery>
  queryData?: Ref<AnyObject>
  pageSize?: number
  /** 挂载时自动发起请求。默认 `true` */
  mountedRequest?: boolean
  formatResponse?: (response: responseType) => {
    list: any[]
    total: number
  }
  formatGetListQuery?: (payload: AnyObject) => AnyObject | false
  /** 监听自动刷新数据的 key */
  refreshKey?: string
}

export default function useList<T = AnyObject>({
  getList,
  pageSize = 10,
  queryData,
  mountedRequest = true,
  formatResponse,
  formatGetListQuery,
  refreshKey,
}: Options) {
  const state = reactive({
    dataList: [] as T[],
    loading: false,
    current: 1,
    total: 0,
    pageSize: pageSize,
  })
  /** 是否已经发起过请求 */
  let isRequest = false

  onMounted(() => {
    if (mountedRequest) {
      queryListData()
    }
  })

  watch(
    () => {
      if (getList && typeof getList !== 'function') {
        return [getList.value]
      }
      return []
    },
    () => {
      onRefresh()
    }
  )

  function getQueryParams(payload: AnyObject) {
    let result
    if (typeof formatGetListQuery === 'function') {
      result = formatGetListQuery(payload)
    } else {
      result = payload
    }
    return result
  }

  /** 请求时序 */
  const queryOrderId = ref(0)
  function queryListData(query?: AnyObject, refresh?: boolean) {
    const payload = getQueryParams({
      ...query,
      ...unref(queryData),
      pageNum: state.current,
      pageSize: state.pageSize,
    })
    if (!payload) {
      return Promise.reject()
    }
    isRequest = true
    return new Promise((resolve, reject) => {
      let getListRq
      if (typeof getList === 'function') {
        getListRq = getList
      } else if (getList && getList.value) {
        getListRq = getList.value
      }

      if (typeof getListRq === 'function') {
        state.loading = true
        const orderId = ++queryOrderId.value

        getListRq(payload)
          .then((response) => {
            if (orderId !== queryOrderId.value) {
              return false
            }
            if (refresh) {
              state.dataList = []
            }

            if (response && response.code === 200) {
              const responseData = handleFormatResponse(response)

              state.dataList = concat(state.dataList, responseData.list)
              state.total = responseData.total

              let status = ''
              if (state.total && state.dataList.length < state.total) {
                status = 'loadmore'
              } else {
                status = 'nomore'
              }

              resolve(status)
            } else {
              resolve('nomore')
            }
          })
          .finally(() => {
            state.loading = false
          })
          .catch((err) => {
            reject(err)
          })
      }
    })
  }

  function handleFormatResponse(response: responseType) {
    if (typeof formatResponse === 'function') {
      return formatResponse(response)
    }

    const rows = get(response, 'data.records', get(response, 'rows', get(response, 'data', response)))
    const total = get(response, 'data.total', get(response, 'total', get(rows, 'length', 0)))

    return {
      list: rows,
      total: total,
    }
  }

  function queryNextPage() {
    return new Promise((resolve, reject) => {
      nextTick(() => {
        if (getList) {
          if (typeof getList === 'function' || typeof getList.value === 'function') {
            if (state.loading) {
              return resolve('loading')
            }
            if (state.dataList.length < state.total || (mountedRequest === false && isRequest === false)) {
              if (isRequest === true) {
                state.current++
              }

              return queryListData()?.then(resolve)
            } else {
              return resolve('nomore')
            }
          }
        }
        return resolve('loadmore')
      })
    })
  }

  function onSearch(query: AnyObject) {
    state.current = 1
    // state.dataList = []
    return queryListData(query, true)
  }

  function onRefresh() {
    // state.dataList = []
    return queryListData({}, true)
  }

  return {
    // pagination: toRefs(state),
    pagination: state,
    loading: toRef(state, 'loading'),
    dataList: toRef(state, 'dataList'),
    onQueryList: onRefresh,
    /** 移动端使用。加载下一页 */
    nextPage: queryNextPage,
    /** 重置页码和已有数据，并重新执行一次查询 */
    onSearch,
    /** 保留页码和已有数据，重新执行一次查询 */
    onRefresh,
  }
}
